/**
 * Small reference lists — departments, shifts, legal entities — for populating pickers and filter
 * dropdowns (the Employee Directory's Department/Shift filters, Add Employee's org-placement
 * fields). Deliberately unpaginated: these are bounded, org-scoped reference data, not directories
 * that grow with headcount — a company might have dozens of departments, not thousands.
 *
 * Every route only needs the read permission already in the base `employee` role tier
 * (`department:read`/`shift:read`/`legal_entity:read`) — this is org-structure metadata visible to
 * everyone, not a permission-gated resource the way employee records are.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, orgIdSchema, uuidSchema } from '../schemas/common.js';
import { employeeCustomFieldOptionSchema } from './employee-custom-fields.contract.js';
import { employeeFieldSectionSchema } from './employee-field-sections.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * Pickers want only active rows, so that is the default. An admin table needs the inactive ones too —
 * otherwise a deactivated department vanishes and can never be re-activated. A string enum rather
 * than a boolean because query strings are text.
 */
export const includeInactiveQuerySchema = z.object({
  includeInactive: z.enum(['true', 'false']).default('false'),
});

export const departmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  parentId: uuidSchema.nullable(),
  /** The head's EMPLOYMENT (the job), not their person — same convention as a branch head. */
  headEmploymentId: uuidSchema.nullable(),
  /** Resolved server-side, so rendering a list needs no request per row. */
  headName: z.string().nullable(),
  headEmployeeCode: z.string().nullable(),
  isActive: z.boolean(),
});
export type Department = z.infer<typeof departmentSchema>;

export const shiftSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  startTime: z.string(),
  endTime: z.string(),
});
export type Shift = z.infer<typeof shiftSchema>;

export const legalEntitySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  country: z.string(),
  currency: z.string(),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  weekStartDay: z.number().int().min(0).max(6),
  timezone: z.string(),
  stateCode: z.string().nullable(),
  city: z.string().nullable(),
  /** Public path of the company's logo, or null. Resolve against the site origin. */
  logoUrl: z.string().nullable(),
  isActive: z.boolean(),
});
export type LegalEntity = z.infer<typeof legalEntitySchema>;

export const locationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  /** The company (legal entity) the location belongs to, so a picker can group by company. */
  legalEntityId: uuidSchema.nullable().optional(),
});
export type Location = z.infer<typeof locationSchema>;

/**
 * Just enough to recognise a colleague in a picker (Task Delegate, "Filing For") — deliberately NOT
 * the Employee Directory's `Employee` shape. No email, phone, salary, or manager chain: this id is
 * exposed org-wide to every authenticated employee (see searchColleagues below), so the DTO itself
 * has to be safe for that, not just the route gating.
 *
 * `id` is the PERSON id, never an Employment id — the same distinction leave.ts's header comment
 * and employee.ts's resolveTarget draw: a delegate/on-behalf-of picker feeds ResourceTarget fields
 * and LeaveRequest.personId, which are Person ids throughout.
 */
export const colleagueSchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable(),
  employeeCode: z.string(),
  departmentName: z.string().nullable(),
});
export type Colleague = z.infer<typeof colleagueSchema>;

export const postSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  title: z.string(),
  departmentId: uuidSchema.nullable(),
  locationId: uuidSchema.nullable(),
  gradeLevel: z.string().nullable(),
  /** The catalogue grade (Organisation → Grades) and its display name, or null. */
  gradeId: uuidSchema.nullable(),
  gradeName: z.string().nullable(),
  isActive: z.boolean(),
});
export type Post = z.infer<typeof postSchema>;

export const assetCategoryLookupSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
});
export type AssetCategoryLookup = z.infer<typeof assetCategoryLookupSchema>;

export const assetTypeLookupSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  categoryId: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export type AssetTypeLookup = z.infer<typeof assetTypeLookupSchema>;

/** Org-customizable offboarding reason — see org-setup.contract.ts's exitTypeSchema for the
 *  admin-managed shape this is read from. */
export const exitTypeLookupSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export type ExitTypeLookup = z.infer<typeof exitTypeLookupSchema>;

/** EXP-P2-12 (Keka parity) — see org-setup.contract.ts's `costCentreSchema` for the admin-managed
 * shape this is read from (active rows only). */
export const costCentreLookupSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export type CostCentreLookup = z.infer<typeof costCentreLookupSchema>;

/** EXP-P2-12 — see project.contract.ts's `projectSchema` for the full PM-module shape this is read
 * from (`status: 'active'` rows only — tagging a claim to a completed/cancelled project is never
 * useful). */
export const projectLookupSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export type ProjectLookup = z.infer<typeof projectLookupSchema>;

/** `key` is the profile-fields.ts key (see built-in-employee-fields.ts's `selfServiceKey`). */
export const selfServiceFieldRuleSchema = z.object({
  key: z.string(),
  requiresApproval: z.boolean(),
});
export type SelfServiceFieldRule = z.infer<typeof selfServiceFieldRuleSchema>;

export const lookupContract = c.router(
  {
    listDepartments: {
      method: 'GET',
      path: '/departments',
      query: includeInactiveQuerySchema,
      responses: { 200: z.array(departmentSchema), 401: errorBodySchema },
      summary: 'Departments (active only unless includeInactive) — for pickers and filters',
      metadata: routeMeta('department:read'),
    },
    listShifts: {
      method: 'GET',
      path: '/shifts',
      responses: { 200: z.array(shiftSchema), 401: errorBodySchema },
      summary: 'Active shifts in the org — for pickers and filters',
      metadata: routeMeta('shift:read'),
    },
    listLegalEntities: {
      method: 'GET',
      path: '/legal-entities',
      query: includeInactiveQuerySchema,
      responses: { 200: z.array(legalEntitySchema), 401: errorBodySchema },
      summary: 'Legal entities (active only unless includeInactive) — for entity pickers',
      metadata: routeMeta('legal_entity:read'),
    },
    listLocations: {
      method: 'GET',
      path: '/locations',
      query: includeInactiveQuerySchema,
      responses: { 200: z.array(locationSchema), 401: errorBodySchema },
      summary: 'Work locations (active only unless includeInactive) — for location pickers',
      metadata: routeMeta('location:read'),
    },
    searchColleagues: {
      method: 'GET',
      path: '/colleagues',
      query: z.object({
        q: z.string().trim().min(1),
        limit: z.coerce.number().int().min(1).max(20).default(8),
      }),
      responses: { 200: z.array(colleagueSchema), 401: errorBodySchema },
      // Org-wide by design, unlike GET /employees: search-as-you-type for Request Leave's Task
      // Delegate and Filing For pickers, which every plain employee must be able to use, not just
      // callers whose `employee:read` grant happens to be org-scoped. See this file's header
      // comment and colleagueSchema's doc comment for why that's safe here.
      summary:
        'Org-wide colleague search by name or employee code — for delegate/on-behalf pickers',
      metadata: routeMeta('employee:read'),
    },
    listPosts: {
      method: 'GET',
      path: '/posts',
      query: includeInactiveQuerySchema,
      responses: { 200: z.array(postSchema), 401: errorBodySchema },
      summary: 'Posts / designations (active only unless includeInactive) — for pickers',
      metadata: routeMeta('post:read'),
    },
    // Unlike every route above (visible to any employee at baseline), Configuration/Vendors are
    // Hidden for Employee per the spec — these two are actually enforced at the API layer with an
    // explicit can() check, not just declared here for the AI-copilot/nav-gating metadata.
    listAssetCategories: {
      method: 'GET',
      path: '/asset-categories',
      responses: { 200: z.array(assetCategoryLookupSchema), 401: errorBodySchema },
      summary: 'Active asset categories in the org — for pickers (e.g. the asset type form)',
      metadata: routeMeta('asset.category:read'),
    },
    listAssetTypes: {
      method: 'GET',
      path: '/asset-types',
      responses: { 200: z.array(assetTypeLookupSchema), 401: errorBodySchema },
      summary: 'Active asset types in the org — for pickers',
      metadata: routeMeta('asset.type:read'),
    },
    // Gated by the same permission `employees.exit` itself requires, not `org.settings:read` —
    // whoever can start an offboarding needs the picker, whether or not they can also manage org
    // settings. Admin CRUD for the list lives in org-setup.contract.ts (listExitTypes there).
    listExitTypes: {
      method: 'GET',
      path: '/exit-types',
      responses: { 200: z.array(exitTypeLookupSchema), 401: errorBodySchema },
      summary: 'Active exit/offboarding reasons — for the Exit Employee picker',
      metadata: routeMeta('employment.exit:execute'),
    },
    // EXP-P2-12 — gated by `expense:submit`, not `org.settings:read`, same reasoning as
    // listExitTypes above: whoever can file a claim needs the picker, whether or not they can
    // also manage org structure. Admin CRUD for cost centres lives in org-setup.contract.ts;
    // projects have their own module (project.contract.ts) — both read-only, active rows only,
    // here.
    listCostCentres: {
      method: 'GET',
      path: '/cost-centres',
      responses: { 200: z.array(costCentreLookupSchema), 401: errorBodySchema },
      summary: 'Active cost centres — for the expense claim cost-centre picker',
      metadata: routeMeta('expense:submit'),
    },
    listProjects: {
      method: 'GET',
      path: '/projects',
      responses: { 200: z.array(projectLookupSchema), 401: errorBodySchema },
      summary: 'Active projects — for the expense claim project picker',
      metadata: routeMeta('expense:submit'),
    },
    // Admin CRUD for the definitions lives in employee-custom-fields.contract.ts, gated
    // org.settings:update — this is the read anyone who can see an employee record needs, to know
    // what extra fields/columns to render on the list, the detail page, and Add/Edit Employee.
    listActiveEmployeeCustomFields: {
      method: 'GET',
      path: '/employee-custom-fields',
      responses: { 200: z.array(employeeCustomFieldOptionSchema), 401: errorBodySchema },
      summary: 'Active org-defined custom fields on the employee list/detail',
      metadata: routeMeta('employee:read'),
    },
    // The sections those fields render in, in display order — built-in Personal / Work / Bank,
    // then the org's own active ones. Admin CRUD is employee-field-sections.contract.ts.
    listActiveEmployeeFieldSections: {
      method: 'GET',
      path: '/employee-field-sections',
      responses: { 200: z.array(employeeFieldSectionSchema), 401: errorBodySchema },
      summary: 'Active employee profile sections, in display order',
      metadata: routeMeta('employee:read'),
    },
    // The EFFECTIVE instant/approval rule (org override, else the profile-fields.ts default) for
    // every field "Edit My Profile" offers — signed-in only, not org.settings:read, because every
    // employee edits their OWN profile regardless of role. Admin CRUD for the override lives on
    // employee-custom-fields.contract.ts's updateEmployeeFieldPolicy.
    listSelfServiceFieldRules: {
      method: 'GET',
      path: '/self-service-field-rules',
      responses: { 200: z.array(selfServiceFieldRuleSchema), 401: errorBodySchema },
      summary: "Effective needs-HR-approval rule per field, for the Edit My Profile form's badges",
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/lookup' },
);
